import { AgPublicEventType, GridApi } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import { isEqual } from "lodash-es";
import { useCallback, useRef, useSyncExternalStore } from "react";
import { tryGetAgGridApi } from "shared/components/table/maybern/MaybernTableUtils";

export const useAgGridEventState = <TData, TResult>(
  agGrid: AgGridReact<TData> | null | undefined,
  eventName: AgPublicEventType,
  handler: (api: GridApi<TData>) => TResult,
): TResult | undefined => {
  const subscribe = useCallback(
    (callback: () => void) => {
      const api = tryGetAgGridApi(agGrid);
      if (!api) {
        return () => {};
      }
      api.addEventListener(eventName, callback);
      return () => {
        const api = tryGetAgGridApi(agGrid);
        api?.removeEventListener(eventName, callback);
      };
    },
    [agGrid, eventName],
  );
  const lastSnapshotRef = useRef<TResult | undefined>();
  const getSnapshot = () => {
    const api = tryGetAgGridApi(agGrid);
    if (!api) {
      return undefined;
    }
    const snapshot = handler(api);
    // https://react.dev/reference/react/useSyncExternalStore#im-getting-an-error-the-result-of-getsnapshot-should-be-cached
    if (!isEqual(snapshot, lastSnapshotRef.current)) {
      lastSnapshotRef.current = snapshot;
    }
    return lastSnapshotRef.current;
  };
  return useSyncExternalStore(subscribe, getSnapshot);
};
